// 指標の説明の吹き出し(metric-tip)の表示位置を決める純関数。
// 座標はすべてビューポート基準(getBoundingClientRect と position: fixed の座標系)。

export const VIEWPORT_MARGIN = 8
export const ANCHOR_GAP = 6

// anchor: { top, bottom, left, width }(アンカー要素の矩形)
// tip: { width, height }(吹き出しの大きさ)
// viewport: { width, height }
// 戻り値: { top, left, placement }(placement は "bottom" | "top")
//
// - アンカーの下に出す。下に収まらず、上のほうが広ければ上に反転する
// - 左右はアンカーの中央にそろえ、ビューポートの端から margin 以上内側に収める
// - 上下とも収まらないときも、上端が margin より上にははみ出さないようにする
export function computeTipPosition({ anchor, tip, viewport, margin = VIEWPORT_MARGIN, gap = ANCHOR_GAP }) {
  const spaceBelow = viewport.height - anchor.bottom - gap - margin
  const spaceAbove = anchor.top - gap - margin
  const placement = tip.height > spaceBelow && spaceAbove > spaceBelow ? "top" : "bottom"

  const rawTop = placement === "bottom" ? anchor.bottom + gap : anchor.top - gap - tip.height
  const top = Math.max(margin, rawTop)

  const centeredLeft = anchor.left + anchor.width / 2 - tip.width / 2
  const maxLeft = Math.max(margin, viewport.width - margin - tip.width)
  const left = Math.min(Math.max(centeredLeft, margin), maxLeft)

  return { top, left, placement }
}

// anchor: { top, bottom, left, right }(アンカー要素の矩形)
// viewport: { width, height }
// clip: { top, bottom, left, right }(スクロールした要素の矩形。ページ自体のスクロールなら省略)
// 戻り値: アンカーが一部でも見えていれば true
//
// スクロール中は吹き出しをアンカーに付いて回らせ、アンカーが見えなくなったら閉じるための判定。
export function isAnchorVisible({ anchor, viewport, clip = null }) {
  const bounds = {
    top: Math.max(0, clip ? clip.top : 0),
    bottom: Math.min(viewport.height, clip ? clip.bottom : viewport.height),
    left: Math.max(0, clip ? clip.left : 0),
    right: Math.min(viewport.width, clip ? clip.right : viewport.width),
  }

  return anchor.bottom > bounds.top && anchor.top < bounds.bottom && anchor.right > bounds.left && anchor.left < bounds.right
}

// キーボードでフォーカスして開いたあと、アンカーが見えていなくても閉じずに付いて回る時間(ミリ秒)。
// html の scroll-behavior: smooth で、Tab で画面外のラベルへ移ったときのブラウザのスクロールが数フレームかけて動く間の猶予
export const FOCUS_SCROLL_GRACE_MS = 800

// スクロール・リサイズのたびに、吹き出しを付いて回らせるか閉じるかを決める。
// anchorVisible: アンカーが一部でも見えているか(isAnchorVisible の結果)
// keyboardFocused: アンカーがキーボードでフォーカスされているか(:focus-visible)
// now: 現在時刻(performance.now())
// focusScrollUntil: キーボードフォーカスで開いたときに記録した猶予の終わり(猶予なしは 0)
// 戻り値: { keepOpen, focusScrollUntil }(focusScrollUntil は次回に使う値)
//
// - アンカーが見えていれば付いて回る。見えたら猶予は使い切り(以降はユーザーのスクロールで見えなくなれば閉じる)
// - 見えていなくても、キーボードフォーカス直後の猶予中(ブラウザの smooth スクロールの途中)なら閉じない
// - それ以外(マウスのクリックで開いた・猶予が切れた・フォーカスが外れた)は閉じる
export function decideOnViewportChange({ anchorVisible, keyboardFocused, now, focusScrollUntil }) {
  if (anchorVisible) return { keepOpen: true, focusScrollUntil: 0 }
  if (keyboardFocused && now < focusScrollUntil) return { keepOpen: true, focusScrollUntil }
  return { keepOpen: false, focusScrollUntil: 0 }
}
